import React, { useState, useEffect } from 'react';
import { db } from '../../firebase';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, Timestamp } from 'firebase/firestore';
import PortalHeader from '../../components/Portal/PortalHeader';
import PsModal from '../../components/Portal/PsModal';
import TestModeBanner from '../../components/TestMode/TestModeBanner';
import './EmailTemplates.css';
import { seedEmailTemplates } from '../../utils/seedEmailTemplates';

const EmailTemplates = () => {
	const [templates, setTemplates] = useState([]);
	const [loading, setLoading] = useState(true);
	const [showEditor, setShowEditor] = useState(false);
	const [editingTemplate, setEditingTemplate] = useState(null);
	const [showPreview, setShowPreview] = useState(false);
	const [previewHtml, setPreviewHtml] = useState('');

	// Form state
	const [templateName, setTemplateName] = useState('');
	const [templateCategory, setTemplateCategory] = useState('custom');
	const [subject, setSubject] = useState('');
	const [htmlContent, setHtmlContent] = useState('');
	const [variables, setVariables] = useState([]);
	const [description, setDescription] = useState('');
	// Bootstrap's <Tabs defaultActiveKey> was uncontrolled; the house .ps-tabs is
	// presentational, so the active tab becomes real state here.
	const [editorTab, setEditorTab] = useState('editor');

	const categories = [
		{ value: 'portal', label: 'Portal Invitations', icon: 'fa-user-plus', color: '#384971' },
		{ value: 'lesson', label: 'Lesson Notes', icon: 'fa-book', color: '#82c6c6' },
		{ value: 'reminder', label: 'Reminders', icon: 'fa-bell', color: '#ffb953' },
		{ value: 'payment', label: 'Payments', icon: 'fa-credit-card', color: '#ed7749' },
		{ value: 'announcement', label: 'Announcements', icon: 'fa-bullhorn', color: '#f933a4' },
		{ value: 'custom', label: 'Custom', icon: 'fa-star', color: '#6c757d' }
	];

	const availableVariables = {
		common: ['studentName', 'parentName', 'parentEmail', 'currentDate'],
		portal: ['loginUrl', 'temporaryPassword'],
		lesson: ['lessonDate', 'lessonTime', 'lessonNotes', 'nextLessonDate', 'assignedMaterials'],
		payment: ['amount', 'paymentDate', 'paymentMethod', 'invoiceNumber', 'balance'],
		announcement: ['title', 'message'],
	};

	useEffect(() => {
		fetchTemplates();
	}, []);

	const fetchTemplates = async () => {
		try {
			const templatesSnapshot = await getDocs(collection(db, 'emailTemplates'));
			const templatesData = templatesSnapshot.docs.map(doc => ({
				id: doc.id,
				...doc.data()
			}));
			setTemplates(templatesData);
		} catch (error) {
			console.error('Error fetching templates:', error);
		} finally {
			setLoading(false);
		}
	};

	const handleCreateNew = () => {
		setEditingTemplate(null);
		setTemplateName('');
		setTemplateCategory('custom');
		setSubject('');
		setHtmlContent(getDefaultTemplate());
		setVariables([]);
		setDescription('');
		setShowEditor(true);
	};

	const handleEdit = (template) => {
		setEditingTemplate(template);
		setTemplateName(template.name);
		setTemplateCategory(template.category || 'custom');
		setSubject(template.subject);
		setHtmlContent(template.htmlContent);
		setVariables(template.variables || []);
		setDescription(template.description || '');
		setShowEditor(true);
	};

	const handleSave = async () => {
		try {
			const templateData = {
				name: templateName,
				category: templateCategory,
				subject: subject,
				htmlContent: htmlContent,
				variables: variables,
				description: description,
				updatedAt: Timestamp.now(),
			};

			if (editingTemplate) {
				// Update existing template
				await updateDoc(doc(db, 'emailTemplates', editingTemplate.id), templateData);
				alert('Template updated successfully!');
			} else {
				// Create new template
				templateData.createdAt = Timestamp.now();
				await addDoc(collection(db, 'emailTemplates'), templateData);
				alert('Template created successfully!');
			}

			setShowEditor(false);
			fetchTemplates();
		} catch (error) {
			console.error('Error saving template:', error);
			alert('Error saving template. Please try again.');
		}
	};

	const handleDelete = async (templateId, templateName) => {
		if (!window.confirm(`Are you sure you want to delete "${templateName}"?`)) return;

		try {
			await deleteDoc(doc(db, 'emailTemplates', templateId));
			alert('Template deleted successfully!');
			fetchTemplates();
		} catch (error) {
			console.error('Error deleting template:', error);
			alert('Error deleting template. Please try again.');
		}
	};

	const handleSeedTemplates = async () => {
		try {
			setLoading(true);
			const result = await seedEmailTemplates();
			if (result.success) {
				alert(result.message);
				fetchTemplates();
			} else {
				alert('Error seeding templates: ' + result.error);
			}
		} catch (error) {
			console.error('Error seeding templates:', error);
			alert('Error seeding templates. Please try again.');
		} finally {
			setLoading(false);
		}
	};

	const handlePreview = () => {
		// Replace variables with sample data for preview
		let preview = htmlContent;
		const sampleData = {
			studentName: 'John Smith',
			parentName: 'Jane Smith',
			parentEmail: 'parent@email.com',
			loginUrl: 'https://your-app.web.app/portal/login',
			temporaryPassword: 'TempPass123!',
			lessonDate: new Date().toLocaleDateString(),
			lessonTime: '3:00 PM',
			lessonNotes: 'Great progress today! Keep practicing scales.',
			nextLessonDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toLocaleDateString(),
			amount: '150.00',
			paymentDate: new Date().toLocaleDateString(),
			paymentMethod: 'Credit Card',
			invoiceNumber: 'INV-2025-001',
			balance: '0.00',
			title: 'Sample Announcement',
			message: 'This is a sample message for preview.',
			currentDate: new Date().toLocaleDateString(),
		};

		Object.keys(sampleData).forEach(key => {
			preview = preview.replace(new RegExp(`{{${key}}}`, 'g'), sampleData[key]);
		});

		setPreviewHtml(preview);
		setShowPreview(true);
	};

	const getDefaultTemplate = () => {
		// Mirrors the live reusable shell (Drive: CYV-Operations/CYV-Email-Template.html).
		// Edit only the EMAIL CONTENT block; keep the logo, gradient rules, signature,
		// and footer as-is. Logo uses the stable public path (never a hashed build path).
		return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1">
  <meta name="x-apple-disable-message-reformatting">
  <meta name="color-scheme" content="light only">
  <meta name="supported-color-schemes" content="light only">
  <title>Color Your Voice</title>
  <style>
    body { margin:0; padding:0; width:100% !important; -webkit-text-size-adjust:100%; -ms-text-size-adjust:100%; }
    table { border-collapse:collapse; mso-table-lspace:0; mso-table-rspace:0; }
    img { border:0; line-height:100%; outline:none; text-decoration:none; -ms-interpolation-mode:bicubic; }
    a { color:#82c6c6; }
    .preheader { display:none !important; visibility:hidden; opacity:0; color:transparent; height:0; width:0; overflow:hidden; mso-hide:all; }
    /* Phone: card fills the screen, padding tightens. */
    @media only screen and (max-width:620px) {
      .card { width:100% !important; max-width:100% !important; }
      .px   { padding-left:16px !important; padding-right:16px !important; }
      .h1   { font-size:25px !important; line-height:1.28 !important; }
      .btn  { display:block !important; width:100% !important; box-sizing:border-box !important; text-align:center !important; }
    }
  </style>
</head>
<body style="margin:0; padding:0; background-color:#f4f4f5;">

  <!-- Preheader: the gray preview text in the inbox list. Edit per send (~90 chars).
       THE HIDING MUST STAY INLINE. Gmail strips <head>/<style>, so a class-only
       hide leaves this line rendering as the first visible text, above the logo
       (found 2026-08-27 on 25 charge-notice drafts). The .preheader rule below is
       kept only for clients that do read <style>; the inline style is what works. -->
  <div class="preheader" style="display:none !important; visibility:hidden; opacity:0; color:transparent; height:0; width:0; max-height:0; max-width:0; overflow:hidden; mso-hide:all; font-size:1px; line-height:1px;">Replace this with one short preview line for the inbox.</div>

  <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" style="background-color:#f4f4f5;">
    <tr>
      <td align="center" style="padding:20px 6px;">

        <!--[if mso | IE]><table role="presentation" width="600" cellpadding="0" cellspacing="0" border="0" align="center"><tr><td><![endif]-->

        <!-- White card (fluid: fills phones, capped at 600px) -->
        <table role="presentation" class="card" width="100%" cellpadding="0" cellspacing="0" border="0" style="width:100%; max-width:600px; background-color:#ffffff; border-radius:14px; overflow:hidden; box-shadow:0 1px 4px rgba(56,73,113,0.08);">

          <!-- Logo -->
          <tr>
            <td class="px" align="center" style="padding:34px 32px 18px 32px;">
              <img src="https://coloryourvoice.com/email-assets/logo.png" alt="Color Your Voice" width="67" height="50" style="width:67px; height:50px; display:block; border:0;">
            </td>
          </tr>

          <!-- Top gradient rule -->
          <tr>
            <td class="px" style="padding:0 32px;">
              <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0">
                <tr><td height="4" bgcolor="#82c6c6" style="height:4px; line-height:4px; font-size:0; background:#82c6c6; background:linear-gradient(90deg,#f0a8c0,#fdc0ae,#82c6c6,#ed7749); border-radius:4px;">&nbsp;</td></tr>
              </table>
            </td>
          </tr>

          <!-- EMAIL CONTENT — replace everything in this block -->
          <tr>
            <td class="px" style="padding:30px 32px 8px 32px; font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;">

              <h1 class="h1" style="margin:0 0 16px 0; font-size:28px; line-height:1.25; color:#384971; font-weight:700;">Your headline goes here</h1>

              <p style="margin:0 0 16px 0; font-size:17px; line-height:1.6; color:#333333;">Dear {{parentName}},</p>

              <p style="margin:0 0 16px 0; font-size:17px; line-height:1.6; color:#333333;">
                Your custom email content goes here. Keep it warm and plain &mdash; a sentence or two on why you're writing, with the most important thing first.
              </p>

            </td>
          </tr>

          <!-- Example: CTA button (navy; goes full-width on phones via .btn) -->
          <tr>
            <td class="px" align="center" style="padding:22px 32px 10px 32px;">
              <table role="presentation" cellpadding="0" cellspacing="0" border="0" align="center">
                <tr>
                  <td bgcolor="#384971" style="border-radius:8px;">
                    <a class="btn" href="https://coloryourvoice.com/login" target="_blank" style="display:inline-block; padding:16px 36px; font-family:'Helvetica Neue',Helvetica,Arial,sans-serif; font-size:17px; font-weight:700; color:#ffffff; text-decoration:none; border-radius:8px;">Button label</a>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
          <!-- END CONTENT -->

          <!-- Bottom gradient rule -->
          <tr>
            <td class="px" style="padding:14px 32px 0 32px;">
              <table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0">
                <tr><td height="4" bgcolor="#82c6c6" style="height:4px; line-height:4px; font-size:0; background:#82c6c6; background:linear-gradient(90deg,#f0a8c0,#fdc0ae,#82c6c6,#ed7749); border-radius:4px;">&nbsp;</td></tr>
              </table>
            </td>
          </tr>

          <!-- Signature -->
          <tr>
            <td class="px" style="padding:20px 32px 30px 32px; font-family:'Helvetica Neue',Helvetica,Arial,sans-serif;">
              <p style="margin:0; font-size:16px; line-height:1.6; color:#333333;">
                <strong style="color:#384971;">Grace &amp; Anthony</strong><br>
                Color Your Voice<br>
                <a href="tel:9522830560" style="color:#82c6c6; text-decoration:none;">(952) 283-0560</a><br>
                <em style="color:#82817f;">A creative harbor where confidence and artistry grow.</em>
              </p>
            </td>
          </tr>

        </table>
        <!-- /White card -->

        <!-- Footer -->
        <table role="presentation" class="card" width="100%" cellpadding="0" cellspacing="0" border="0" style="width:100%; max-width:600px;">
          <tr>
            <td class="px" style="padding:18px 32px 0 32px; font-family:'Helvetica Neue',Helvetica,Arial,sans-serif; text-align:center;">
              <p style="margin:0 0 6px 0; font-size:13px; line-height:1.5; color:#9a9a9a;">
                Color Your Voice &nbsp;&middot;&nbsp; inside Headline Dance Center &nbsp;&middot;&nbsp; 12004 12th Ave S, Burnsville, MN 55337
              </p>
              <p style="margin:0; font-size:13px; line-height:1.5; color:#9a9a9a;">
                You're receiving this because you're part of the Color Your Voice family. Prefer not to get these? Just reply and we'll take you off the list.
              </p>
            </td>
          </tr>
        </table>

        <!--[if mso | IE]></td></tr></table><![endif]-->

      </td>
    </tr>
  </table>

</body>
</html>`;
	};

	const getCategoryInfo = (categoryValue) => {
		return categories.find(c => c.value === categoryValue) || categories[categories.length - 1];
	};

	if (loading) {
		return (
			<>
				<PortalHeader title='Email Templates' subtitle='Loading templates...' />
				<TestModeBanner />
				<div className='ps-page'>
					<div className='ps-spinner' role='status' aria-label='Loading' />
				</div>
			</>
		);
	}

	return (
		<>
			<PortalHeader title='Email Templates' subtitle='Manage and customize your email templates' />
			<TestModeBanner />
			<div className='ps-page'>
				<div className='et-header'>
					<span className='ps-badge ps-badge--neutral'>{templates.length} Templates</span>
					<button type='button' className='ps-btn ps-btn--primary' onClick={handleCreateNew}>
						<i className='fa-sharp fa-solid fa-plus'></i>
						Create New Template
					</button>
				</div>

				{templates.length === 0 ? (
					<div className='ps-alert ps-alert--info'>
						<strong>No Templates Yet</strong>
						<p>You haven't created any custom email templates yet.</p>
						<p>Get started by loading our default templates or creating your own from scratch.</p>
						<div className='et-card__actions'>
							<button type='button' className='ps-btn ps-btn--primary' onClick={handleSeedTemplates}>
								<i className='fa-sharp fa-solid fa-download'></i>
								Load Default Templates
							</button>
							<button type='button' className='ps-btn' onClick={handleCreateNew}>
								<i className='fa-sharp fa-solid fa-plus'></i>
								Create Custom Template
							</button>
						</div>
					</div>
				) : (
					<div className='et-grid'>
						{templates.map(template => {
							const categoryInfo = getCategoryInfo(template.category);
							return (
								<div
									className='ps-card et-card'
									key={template.id}
									style={{ '--et-cat': categoryInfo.color }}>
									<div className='et-card__head'>
										<i
											className={`fa-sharp fa-solid ${categoryInfo.icon} et-card__icon`}
											style={{ color: categoryInfo.color }}
											aria-hidden='true'
										></i>
										<span className='ps-badge et-cat-badge'>{categoryInfo.label}</span>
									</div>
									<h3 className='et-card__title'>{template.name}</h3>
									<p className='et-card__desc'>
										{template.description || 'No description provided'}
									</p>
									<div className='et-card__field'>
										<strong>Subject:</strong>
										<div className='et-card__value'>{template.subject}</div>
									</div>
									{template.variables && template.variables.length > 0 && (
										<div className='et-card__field'>
											<strong>Variables:</strong>
											<div className='et-vars'>
												{template.variables.map((v, idx) => (
													<span className='ps-badge ps-badge--neutral' key={idx}>
														{`{{${v}}}`}
													</span>
												))}
											</div>
										</div>
									)}
									<div className='et-card__actions'>
										<button type='button' className='ps-btn' onClick={() => handleEdit(template)}>
											<i className='fa-sharp fa-solid fa-edit'></i>
											Edit
										</button>
										<button
											type='button'
											className='ps-btn ps-btn--danger'
											onClick={() => handleDelete(template.id, template.name)}
										>
											<i className='fa-sharp fa-solid fa-trash'></i>
											Delete
										</button>
									</div>
									<div className='et-card__meta'>
										Updated: {template.updatedAt?.toDate().toLocaleDateString() || 'Unknown'}
									</div>
								</div>
							);
						})}
					</div>
				)}

				{/* Template Editor Modal. PsModal supplies overlay, ESC, backdrop
				    close and dialog semantics — see its header for what it does NOT
				    do (focus trap). */}
				<PsModal
					open={showEditor}
					onClose={() => setShowEditor(false)}
					title={editingTemplate ? 'Edit Template' : 'Create New Template'}
					titleIcon='fa-code'
					className='et-modal'
					width='900px'
					footer={
						<>
							<button type='button' className='ps-btn' onClick={() => setShowEditor(false)}>
								Cancel
							</button>
							<button
								type='button'
								className='ps-btn ps-btn--primary'
								onClick={handleSave}
								disabled={!templateName || !subject || !htmlContent}>
								<i className='fa-sharp fa-solid fa-save'></i>
								{editingTemplate ? 'Update Template' : 'Create Template'}
							</button>
						</>
					}>
					<div className='ps-tabs' role='tablist'>
						{[
							{ key: 'editor', label: 'Editor' },
							{ key: 'variables', label: 'Available Variables' },
							{ key: 'help', label: 'Help & Tips' },
						].map((t) => (
							<button
								key={t.key}
								type='button'
								role='tab'
								aria-selected={editorTab === t.key}
								className={`ps-tab${editorTab === t.key ? ' is-active' : ''}`}
								onClick={() => setEditorTab(t.key)}>
								{t.label}
							</button>
						))}
					</div>

					{editorTab === 'editor' && (
						<>
							<div className='et-modal__row'>
								<div className='ps-field'>
									<label className='ps-label' htmlFor='et-name'>
										<strong>Template Name</strong>
									</label>
									<input
										id='et-name'
										type='text'
										className='ps-input'
										value={templateName}
										onChange={(e) => setTemplateName(e.target.value)}
										placeholder='e.g., Welcome Email, Lesson Summary'
									/>
								</div>
								<div className='ps-field'>
									<label className='ps-label' htmlFor='et-category'>
										<strong>Category</strong>
									</label>
									<select
										id='et-category'
										className='ps-select'
										value={templateCategory}
										onChange={(e) => setTemplateCategory(e.target.value)}>
										{categories.map(cat => (
											<option key={cat.value} value={cat.value}>{cat.label}</option>
										))}
									</select>
								</div>
							</div>

							<div className='ps-field'>
								<label className='ps-label' htmlFor='et-subject'>
									<strong>Email Subject</strong>
								</label>
								<input
									id='et-subject'
									type='text'
									className='ps-input'
									value={subject}
									onChange={(e) => setSubject(e.target.value)}
									placeholder='You can use variables like: Welcome {{studentName}}'
								/>
							</div>

							<div className='ps-field'>
								<label className='ps-label' htmlFor='et-description'>
									<strong>Description</strong>
								</label>
								<input
									id='et-description'
									type='text'
									className='ps-input'
									value={description}
									onChange={(e) => setDescription(e.target.value)}
									placeholder='Brief description of when to use this template'
								/>
							</div>

							<div className='ps-field'>
								<label className='ps-label' htmlFor='et-html'>
									<strong>HTML Content</strong>
								</label>
								<textarea
									id='et-html'
									className='ps-textarea et-code'
									rows={15}
									value={htmlContent}
									onChange={(e) => setHtmlContent(e.target.value)}
									placeholder='Enter your HTML email template here...'
								/>
							</div>

							<button type='button' className='ps-btn' onClick={handlePreview}>
								<i className='fa-sharp fa-solid fa-eye'></i>
								Preview Template
							</button>
						</>
					)}

					{editorTab === 'variables' && (
						<>
							<div className='ps-alert ps-alert--info'>
								<strong>How to use variables:</strong>
								<br />
								Use double curly braces in your template like this:{' '}
								<code>{`{{variableName}}`}</code>
							</div>

							{Object.keys(availableVariables).map(category => (
								<div key={category} className='ps-field'>
									<strong className='ps-label'>{category} Variables:</strong>
									<div className='et-varlist'>
										{availableVariables[category].map(variable => (
											<span className='ps-badge ps-badge--neutral' key={variable}>
												{`{{${variable}}}`}
											</span>
										))}
									</div>
								</div>
							))}
						</>
					)}

					{editorTab === 'help' && (
						<>
							<strong className='ps-label'>Creating Email Templates:</strong>
							<ul>
								<li>Use HTML for formatting</li>
								<li>Include variables using {`{{variableName}}`} syntax</li>
								<li>Keep templates mobile-responsive</li>
								<li>Test with preview before saving</li>
								<li>Use inline CSS styles for best email client compatibility</li>
							</ul>

							<strong className='ps-label'>Example Template Structure:</strong>
							<pre className='et-example'>{`<p>Dear {{parentName}},</p>
<p>This is regarding {{studentName}}'s lesson on {{lessonDate}}.</p>
<p>Best regards,<br>Color Your Voice</p>`}</pre>
						</>
					)}
				</PsModal>

				{/* Preview Modal. The preview body is INTENTIONALLY light in every
				    theme — it renders the real email, whose shell declares
				    `color-scheme: light only`. A themed preview would misrepresent
				    what the parent receives. See EmailTemplates.css → .et-preview. */}
				<PsModal
					open={showPreview}
					onClose={() => setShowPreview(false)}
					title='Template Preview (with sample data)'
					width='720px'
					footer={
						<button type='button' className='ps-btn' onClick={() => setShowPreview(false)}>
							Close
						</button>
					}>
					<div className='et-preview' dangerouslySetInnerHTML={{ __html: previewHtml }} />
				</PsModal>
			</div>
		</>
	);
};

export default EmailTemplates;
